import { FunctionComponent, lazy, PropsWithChildren, useContext } from 'react';

import { RenderingAnchorContext } from '../RenderingAnchor/rendering.provider';
import { IRegisteredComponent } from '../RenderingAnchor/rendering.provider.model';
import { Anchors } from './anchors';
import { componentModeActivationFn } from './component-mode.activationfn';
import { datamapperActivationFn } from './datamapper.activationfn';
import { groupAutoStartupActivationFn } from './group-auto-startup.activationfn';
import { getPluginComponents } from './plugin-registry';

interface IRegisterComponents {
  additionalComponents?: IRegisteredComponent[];
}

/**
 * Created once, at module scope, because the provider recognises an already-registered component
 * by its identity. A `lazy()` wrapper built inside this component is a new object on every mount,
 * which that check cannot match — so each remount registered another copy and the anchor rendered
 * the same component two, three, four times over.
 */
const BUILT_IN_COMPONENTS: IRegisteredComponent[] = [
  {
    anchor: Anchors.CanvasFormHeader,
    activationFn: datamapperActivationFn,
    component: lazy(() => import('../DataMapper/DataMapperLauncher')),
  },
  {
    anchor: Anchors.CanvasFormHeader,
    activationFn: componentModeActivationFn,
    component: lazy(() => import('../ComponentMode/ComponentMode')),
  },
  {
    anchor: Anchors.CanvasGroupTitlebar,
    activationFn: groupAutoStartupActivationFn,
    component: lazy(() => import('../GroupAutoStartupSwitch/GroupAutoStartupSwitch')),
  },
];

export const RegisterComponents: FunctionComponent<PropsWithChildren<IRegisterComponents>> = ({
  children,
  additionalComponents,
}) => {
  const { registerComponent } = useContext(RenderingAnchorContext);

  BUILT_IN_COMPONENTS.concat(getPluginComponents(), additionalComponents ?? []).forEach((regComponent) => {
    registerComponent(regComponent);
  });

  return <>{children}</>;
};
